import {
  Popover,
  PopoverContent,
  PlainButton,
  Heading,
  Stack,
  InlineStack,
  Text,
  Label,
  Link,
} from '@customerio/pluma-components/react';
import { useState } from 'react';
import {
  HealthScoreGauge,
  useWorkspaceHealthSimulation,
  useEnvHealth,
  getHealthSummary,
} from '@/features/workspace-health';
import { PlumaButton as ButtonCss } from '@customerio/pluma-components/css/components';
import clsx from 'clsx';
import * as styles from '@/layouts/authenticated/components/top-navigation/components/icon-button/index.css';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { calc } from '@vanilla-extract/css-utils';
import { useWorkspaceDetails } from '@/features/masquerade-banner/hooks';
import { isEmptyString } from '@/utils/string';

export function WorkspaceHealthNotification(props?: { isNewLayout?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const { track } = useAnalytics();
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
  const isIdentifiersMigrationInProgress =
    isEmptyString(workspaceDetailsQuery.data?.identifiers_migration_in_progress) === false;
  const data = healthQuery.data?.body;

  const { heading, severities, hasIssues, info } = getHealthSummary(data, isIdentifiersMigrationInProgress);

  const healthLink = `/workspaces/${workspaceId}/health`;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      void track('workspaceHealth', 'notificationClicked', {});
    }
    setIsOpen(open);
  };

  return (
    <Popover isOpen={isOpen} onOpenChange={handleOpenChange} withArrow={false} placement="bottom-end">
      {(context) => (
        <PlainButton
          className={clsx(
            props?.isNewLayout === true ? undefined : styles.iconButton,
            ButtonCss.classNames.button,
            ButtonCss.classNames.buttonVariantSubtle,
            ButtonCss.classNames.buttonToneDefault,
            ButtonCss.classNames.buttonSizeSmall,
            ButtonCss.classNames.buttonLegacy,
          )}
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          aria-label="Workspace health"
        >
          <HealthScoreGauge size={16} stroke={2} showIcon={false} />
        </PlainButton>
      )}

      <PopoverContent style={{ width: calc(themeVars.space['2400']).multiply(2).toString() }}>
        <InlineStack gap="200">
          <HealthScoreGauge size={48} stroke={4} iconSize="md" />
          <Stack gap="100">
            <Heading level="3">{heading}</Heading>
            {info.length > 0
              ? info.map((item) => (
                  <Text color="subtle" size="s" key={item}>
                    {item}
                  </Text>
                ))
              : null}
            {hasIssues ? (
              <InlineStack gap="100" wrap>
                {severities.high > 0 ? <Label color="red">{severities.high} high priority</Label> : null}
                {severities.medium > 0 ? <Label color="yellow">{severities.medium} medium</Label> : null}
                {severities.low > 0 ? <Label color="grey">{severities.low} low</Label> : null}
              </InlineStack>
            ) : null}
            <div>
              <Link
                href={healthLink}
                icon="chevron-right"
                iconPosition="trailing"
                onClick={() => {
                  void track('workspaceHealth', 'notificationLinkClicked', {});
                  setIsOpen(false);
                }}
              >
                Explore performance
              </Link>
            </div>
          </Stack>
        </InlineStack>
      </PopoverContent>
    </Popover>
  );
}
